"use client";

import * as React from "react";
import { type Citation, getType } from "@quovy/field-types";
import { TruncatedValue } from "../data-display/truncated-value";
import { cn } from "../utils";

export interface ValueDisplayProps {
  /** Field-type name registered in `@quovy/field-types`. */
  type: string;
  value: unknown;
  options?: unknown;
  citations?: Citation[];
  className?: string;
}

/**
 * Registry-first read-only renderer. Resolves the entry from
 * `@quovy/field-types` and delegates to its `render`. For `text`,
 * the raw string is wrapped in `TruncatedValue` to preserve the
 * single-line hover-overflow behavior that `CellValueText` provides.
 *
 * NOTE: Callers must render this inside an existing `@container`
 * wrapper. We do not introduce a new container here so the cell's
 * width signals continue to govern truncation behavior.
 */
export function ValueDisplay({ type, value, options, citations, className }: ValueDisplayProps) {
  const entry = getType(type);
  const node = entry.display({
    value,
    ...(options !== undefined ? { options } : {}),
    ...(citations !== undefined ? { citations } : {}),
  });

  // The registry entry's `wrap` flag drives cell-level wrapping. `truncate`
  // wraps the node in single-line `TruncatedValue` with hover overflow —
  // the same behavior `CellValueText` provides. Anything else is rendered
  // as-is, leaving any cell-level wrapping to the registry entry itself.
  if (entry.wrap === "truncate") {
    if (node == null || node === "") return null;
    return (
      <TruncatedValue className={cn("text-sm", className)}>
        {node as React.ReactNode}
      </TruncatedValue>
    );
  }

  return <>{node}</>;
}
